import { FetchApiError } from '@/utils/apiFetch';
import UseJwtAuth from '@auth/services/jwt/useJwtAuth';
import Link from '@fuse/core/Link';
import { zodResolver } from '@hookform/resolvers/zod';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Alert, CircularProgress, IconButton, InputAdornment } from '@mui/material';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { isEmpty } from 'lodash';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { z } from 'zod';

/** Start of Key Flow of Password Reset */
const keySchema = z.object({
    key: z.string(),
    password: z.string().min(8, 'Password must be at least 8 characters'),
    confirm_password: z.string().min(8, 'Password must be at least 8 characters'),
});

type keyFormType = z.infer<typeof keySchema>;

const keyDefaults = {
    key: '',
    password: '',
    confirm_password: ''
};

/**
 * Component for resetting a user's password.
 * 
 * This form is used in the password reset flow, allowing users to set a new password
 * after receiving a reset token via email. The component:
 * - Validates that the password meets minimum requirements (8+ characters)
 * - Ensures the confirmation password matches
 * - Handles the password reset API call
 * - Shows success message after successful reset
 * - Provides password visibility toggle options
 * - Validates the presence of a reset token in URL query parameters
 * 
 * @returns A React component that renders the password reset form or success message
 */
function PasswordResetForm() {

    const [loading, setLoading] = useState<boolean>(false);
    const [passwordVisibility, setPasswordVisibility] = useState<boolean[]>([false, false]);
    const [showAlert, setShowAlert] = useState<boolean>(false);
    const { resetPassword } = UseJwtAuth();
    const searchParams = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');

    const key = searchParams.get('key') || '';

    const keyForm = useForm<keyFormType>({
        resolver: zodResolver(keySchema),
        defaultValues: keyDefaults,
        mode: 'onChange'
    });

    const onSubmit = async () => {
        setLoading(true);

        resetPassword({
            password: keyForm.getValues().password,
            confirm_password: keyForm.getValues().confirm_password,
            token: key
        })
            .then(() => {
                toast.success('Password reset successfully');
                setShowAlert(true);
            })
            .catch((error: FetchApiError) => {
                const err = error.data as {
                    detail?: string | { non_field_errors?: string[]; password?: string[]; confirm_password?: string[]; };
                } | undefined;

                if (typeof err?.detail === 'string') {
                    keyForm.setError('password', {
                        type: 'server',
                        message: err.detail
                    });
                } else if (typeof err?.detail === 'object') {
                    const message =
                        err.detail.non_field_errors?.[0] ??
                        err.detail.password?.[0] ??
                        err.detail.confirm_password?.[0];

                    if (message) {
                        keyForm.setError('password', {
                            type: 'server',
                            message
                        });
                    }
                }
            })
            .finally(() => {
                setLoading(false);
            });
    };

    keyForm.setValue('key', key);

    if (showAlert) {
        return <Alert severity="success" className="mt-12">
            Your password has been reset successfully. You can now <Link to="/sign-in" className="font-bold">sign in</Link> with your new password.
        </Alert>;
    }

    if (key)
        return <form
            id='reset-password'
            noValidate
            className="mt-12 flex w-full flex-col justify-center space-y-10"
            onSubmit={keyForm.handleSubmit(onSubmit)}
        >
            <Controller
                name="password"
                control={keyForm.control}
                render={({ field }) => (
                    <TextField
                        {...field}
                        className="relative"
                        label="New Password"
                        type={passwordVisibility[0] ? 'text' : 'password'}
                        error={!!keyForm.formState.errors.password}
                        helperText={keyForm.formState.errors?.password?.message}
                        variant="outlined"
                        required
                        fullWidth
                        slotProps={{
                            input: {
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label={
                                                passwordVisibility[0] ? 'hide the password' : 'display the password'
                                            }
                                            onClick={() => setPasswordVisibility([!passwordVisibility[0], passwordVisibility[1]])}
                                            onMouseDown={(event: React.MouseEvent<HTMLButtonElement>) =>
                                                event.preventDefault()
                                            }
                                            onMouseUp={(event: React.MouseEvent<HTMLButtonElement>) =>
                                                event.preventDefault()
                                            }
                                            edge="end"
                                        >
                                            {passwordVisibility[0] ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }
                        }}
                    />
                )}
            />

            <Controller
                name="confirm_password"
                control={keyForm.control}
                render={({ field }) => (
                    <TextField
                        {...field}
                        className="relative"
                        label="Confirm new Password"
                        type={passwordVisibility[1] ? 'text' : 'password'}
                        error={!!keyForm.formState.errors.password}
                        helperText={keyForm.formState.errors?.password?.message}
                        variant="outlined"
                        required
                        fullWidth
                        slotProps={{
                            input: {
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label={
                                                passwordVisibility[1] ? 'hide the password' : 'display the password'
                                            }
                                            onClick={() => setPasswordVisibility([passwordVisibility[0], !passwordVisibility[1]])}
                                            onMouseDown={(event: React.MouseEvent<HTMLButtonElement>) =>
                                                event.preventDefault()
                                            }
                                            onMouseUp={(event: React.MouseEvent<HTMLButtonElement>) =>
                                                event.preventDefault()
                                            }
                                            edge="end"
                                        >
                                            {passwordVisibility[1] ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }
                        }}
                    />
                )}
            />

            <Button
                variant="contained"
                color="secondary"
                className=" w-full mt-24"
                aria-label="Sign in"
                disabled={isEmpty(
                    keyForm.formState.dirtyFields) || !keyForm.formState.isValid || loading}
                type="submit"
                size="large"
            >
                {loading && (
                    <CircularProgress
                        size={15}
                        className="mr-5"
                    />
                )}
                Reset Password
            </Button>
        </form>;
}

export default PasswordResetForm;